import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiAlertCircle, FiCheckCircle, FiArrowLeft, FiX } from "react-icons/fi";
import {
  orcamentoFornecedorService,
  ApiOrcamentoResponse,
} from "@services/orcamentoFornecedorService";
import {
  formatCodigoOrcamento,
  formatPeriodoEvento,
} from "../utils/orcamento-formatters";
import { OrcamentoHeader } from "../components/orcamento-fornecedor/OrcamentoHeader";
import { jsPDF } from "jspdf";

export const RecusarOrcamento: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>("");
  const [orcamento, setOrcamento] = useState<ApiOrcamentoResponse | null>(null);
  const [motivoSelecionado, setMotivoSelecionado] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    loadOrcamento();
  }, [token]);

  const loadOrcamento = async () => {
    if (!token) {
      setError("Token de orçamento não encontrado");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await orcamentoFornecedorService.carregarOrcamento(token);
      setOrcamento(data);
      setError("");
    } catch (err: any) {
      console.error("Erro ao carregar orçamento:", err);
      setError(
        err.response?.data?.message ||
          "Erro ao carregar dados do orçamento. Verifique se o link está correto.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleJustificativaChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setJustificativa(value);

    // Validação em tempo real apenas se "Outro motivo" estiver selecionado
    if (motivoSelecionado === "outro") {
      if (value.trim().length < 10) {
        setValidationError("A justificativa deve ter no mínimo 10 caracteres");
      } else if (value.length > 1000) {
        setValidationError(
          "A justificativa deve ter no máximo 1000 caracteres",
        );
      } else {
        setValidationError("");
      }
    }
  };

  const handleMotivoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const motivo = e.target.value;
    setMotivoSelecionado(motivo);
    setValidationError("");

    // Limpar justificativa se não for "outro"
    if (motivo !== "outro") {
      setJustificativa("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar se um motivo foi selecionado
    if (!motivoSelecionado) {
      setValidationError("Selecione um motivo para a recusa");
      return;
    }

    // Validação final para "Outro motivo"
    if (motivoSelecionado === "outro") {
      if (justificativa.trim().length < 10) {
        setValidationError("A justificativa deve ter no mínimo 10 caracteres");
        return;
      }

      if (justificativa.length > 1000) {
        setValidationError(
          "A justificativa deve ter no máximo 1000 caracteres",
        );
        return;
      }
    }

    try {
      setSubmitting(true);
      setError("");
      setValidationError("");

      // Definir a justificativa final baseada na seleção
      let justificativaFinal = "";
      if (motivoSelecionado === "prazo") {
        justificativaFinal = "Não consigo atender ao prazo";
      } else if (motivoSelecionado === "documento") {
        justificativaFinal = "Não emito o documento fiscal exigido";
      } else {
        justificativaFinal = justificativa.trim();
      }

      await orcamentoFornecedorService.rejeitarOrcamento(token!, {
        justificativa: justificativaFinal,
      });

      // Gerar e fazer upload do PDF de rejeição (não bloqueia o fluxo em caso de erro)
      try {
        await gerarEUploadPdfRejeicao(justificativaFinal);
      } catch (pdfErr) {
        console.error("Erro ao gerar/upload PDF de rejeição:", pdfErr);
      }

      setSuccess(true);
    } catch (err: any) {
      console.error("Erro ao rejeitar orçamento:", err);
      setError(
        err.response?.data?.message ||
          "Erro ao enviar recusa. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/orcamento/${token}`);
  };

  const gerarEUploadPdfRejeicao = async (justificativaFinal: string) => {
    if (!orcamento) return;

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    const f = orcamento.fornecedor;
    const codigoFormatado = formatCodigoOrcamento(orcamento.codigoSequencial);

    // ─── Pré-calcular nome da empresa para determinar altura do cabeçalho ────
    const maxNameWidth = pageWidth - margin * 2;
    const companyName = f.razaoSocial || f.nome || "-";
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const nameLines = doc.splitTextToSize(companyName, maxNameWidth);
    const nameLineCount = Math.min(nameLines.length, 2);

    // Calcular altura dinâmica do cabeçalho
    let headerContentEndY = 13 + nameLineCount * 6;
    if (f.cnpj || f.telefone) headerContentEndY += 5;
    if (f.endereco) headerContentEndY += 5;
    if (f.email) headerContentEndY += 5;
    const headerHeight = headerContentEndY + 9;

    // ─── Cabeçalho com fundo escuro ──────────────────────────────────────────
    doc.setFillColor(22, 26, 35);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    // Faixa de acento na base do cabeçalho
    doc.setFillColor(60, 65, 80);
    doc.rect(0, headerHeight - 3.5, pageWidth, 3.5, "F");

    // Nome da empresa
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(nameLines.slice(0, 2), margin, 13);

    // Detalhes de contato
    let infoY = 13 + nameLineCount * 6;
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(155, 160, 175);

    const contactParts: string[] = [];
    if (f.cnpj) contactParts.push(`CNPJ: ${f.cnpj}`);
    if (f.telefone) contactParts.push(`Tel: ${f.telefone}`);
    if (contactParts.length > 0) {
      doc.text(contactParts.join("   |   "), margin, infoY);
      infoY += 5;
    }
    if (f.endereco) {
      doc.text(f.endereco, margin, infoY);
      infoY += 5;
    }
    if (f.email) {
      doc.text(`E-mail: ${f.email}`, margin, infoY);
    }

    // ─── Helpers do corpo ─────────────────────────────────────────────────────
    let y = headerHeight + 12;

    const setSection = (title: string, accent = false) => {
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "bold");
      if (accent) {
        doc.setTextColor(213, 38, 38);
      } else {
        doc.setTextColor(110, 115, 125);
      }
      doc.text(title.toUpperCase(), margin, y);
      y += 2.5;
      if (accent) {
        doc.setDrawColor(213, 38, 38);
      } else {
        doc.setDrawColor(225, 225, 230);
      }
      doc.setLineWidth(0.3);
      doc.line(margin, y, margin + contentWidth, y);
      y += 6;
      doc.setLineWidth(0.2);
    };

    const addField = (label: string, value: string) => {
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(115, 115, 120);
      doc.text(label, margin, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(25, 25, 30);
      const lines = doc.splitTextToSize(value, contentWidth - 34);
      doc.text(lines, margin + 34, y);
      y += lines.length * 4.5 + 2;
    };

    // ─── Identificação do Documento ───────────────────────────────────────────
    setSection("Identificação do Documento");
    addField("Código:", codigoFormatado);
    addField("Gerado em:", new Date().toLocaleString("pt-BR"));
    y += 5;

    // ─── Dados do Evento ──────────────────────────────────────────────────────
    setSection("Dados do Evento");
    addField("Evento:", orcamento.planilha?.evento || "-");
    addField("Cliente:", orcamento.planilha?.cliente?.nome || "-");
    if (orcamento.planilha?.dataInicio) {
      addField(
        "Período:",
        formatPeriodoEvento(
          orcamento.planilha.dataInicio,
          orcamento.planilha.dataFim,
        ) || "-",
      );
    }
    y += 5;

    // ─── Itens Solicitados ────────────────────────────────────────────────────
    if (orcamento.itens && orcamento.itens.length > 0) {
      setSection("Itens Solicitados");
      orcamento.itens.forEach((item, idx) => {
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(25, 25, 30);
        const descricao = item.descricao || item.descritivo || "-";
        const lines = doc.splitTextToSize(
          `${idx + 1}.  ${descricao}`,
          contentWidth - 2,
        );
        doc.text(lines, margin, y);
        y += lines.length * 4.5 + 1.5;
      });
      y += 5;
    }

    // ─── Motivo da Recusa — destaque com acento vermelho ─────────────────────
    setSection("Motivo da Recusa", true);

    const justLines = doc.splitTextToSize(
      justificativaFinal,
      contentWidth - 12,
    );
    const blockHeight = justLines.length * 4.5 + 12;

    // Caixa com fundo vermelho suave
    doc.setFillColor(255, 245, 245);
    doc.setDrawColor(252, 200, 200);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y - 2, contentWidth, blockHeight, 2, 2, "FD");

    // Barra lateral vermelha
    doc.setFillColor(213, 38, 38);
    doc.rect(margin, y - 2, 4, blockHeight, "F");

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(30, 30, 35);
    doc.text(justLines, margin + 9, y + 4.5);
    y += blockHeight + 8;

    // ─── Rodapé ───────────────────────────────────────────────────────────────
    doc.setFillColor(246, 246, 248);
    doc.rect(0, pageHeight - 12, pageWidth, 12, "F");
    doc.setDrawColor(215, 215, 220);
    doc.setLineWidth(0.3);
    doc.line(0, pageHeight - 12, pageWidth, pageHeight - 12);

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(145, 145, 150);
    doc.text("Sistema de Cotações – Monumenta", margin, pageHeight - 4.5);
    doc.text(
      `${codigoFormatado} · Recusa de cotação`,
      pageWidth - margin,
      pageHeight - 4.5,
      { align: "right" },
    );

    const pdfBase64 = doc.output("datauristring");
    await orcamentoFornecedorService.uploadPdfRejeicao(token!, pdfBase64);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg"></span>
          <p className="mt-4 text-lg">Carregando orçamento...</p>
        </div>
      </div>
    );
  }

  if (error && !orcamento) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg border-2 border-red-500 p-8">
            <div className="text-center">
              <FiAlertCircle className="mx-auto text-red-600 w-16 h-16 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Erro ao Carregar Orçamento
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                className="btn btn-primary"
                onClick={() => window.location.reload()}
              >
                Tentar Novamente
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Verificar se orçamento já foi rejeitado
  if (orcamento?.status === "rejeitado") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg border-2 border-red-500 p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <FiX className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Orçamento Rejeitado
              </h2>
              <p className="text-gray-600 mb-6">
                Este orçamento já foi rejeitado anteriormente. Não é possível
                rejeitá-lo novamente.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  <strong>Código do Orçamento:</strong>{" "}
                  {formatCodigoOrcamento(orcamento.codigoSequencial)}
                </p>
                <p className="text-sm text-red-800 mt-1">
                  <strong>Fornecedor:</strong>{" "}
                  {orcamento.fornecedor.nome ||
                    orcamento.fornecedor.razaoSocial}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Em caso de dúvidas, entre em contato com a Monumenta.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Verificar se orçamento já foi concluído
  if (orcamento?.status === "concluido") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg border-2 border-orange-500 p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-orange-100 mb-4">
                <FiAlertCircle className="h-10 w-10 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Orçamento Já Respondido
              </h2>
              <p className="text-gray-600 mb-6">
                Este orçamento já foi respondido e finalizado. Não é possível
                rejeitá-lo após o envio da resposta.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-orange-800">
                  <strong>Código do Orçamento:</strong>{" "}
                  {formatCodigoOrcamento(orcamento.codigoSequencial)}
                </p>
                <p className="text-sm text-orange-800 mt-1">
                  <strong>Fornecedor:</strong>{" "}
                  {orcamento.fornecedor.nome ||
                    orcamento.fornecedor.razaoSocial}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Em caso de dúvidas, entre em contato com a Monumenta.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg border-2 border-green-500 p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <FiCheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Cotação Recusada com Sucesso
              </h2>
              <p className="text-gray-600 mb-6">
                O solicitante foi notificado por email sobre a sua recusa.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <OrcamentoHeader
        codigoOrcamento={formatCodigoOrcamento(orcamento!.codigoSequencial)}
        nomeEvento={orcamento!.planilha.evento}
        nomeCliente={orcamento!.planilha.cliente.nome}
        dataInicio={orcamento!.planilha.dataInicio}
        dataFim={orcamento!.planilha.dataFim}
        localEvento={orcamento!.planilha.localEvento}
      />

      {/* Container Principal */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
          {/* Título da ação */}
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-red-100 shrink-0">
              <FiX className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Recusar Cotação
              </h2>
              <p className="text-sm text-gray-500">
                Informe o motivo pelo qual não pode atender esta solicitação.
              </p>
            </div>
          </div>

          {/* Informações do Orçamento */}
          <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Informações do Orçamento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Código</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatCodigoOrcamento(orcamento!.codigoSequencial)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Evento</p>
                <p className="text-base font-semibold text-gray-900">
                  {orcamento!.planilha.evento}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Cliente</p>
                <p className="text-base font-semibold text-gray-900">
                  {orcamento!.planilha.cliente.nome}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Período</p>
                <p className="text-base font-semibold text-gray-900">
                  {formatPeriodoEvento(
                    orcamento!.planilha.dataInicio,
                    orcamento!.planilha.dataFim,
                  ) || "Não informado"}
                </p>
              </div>
            </div>

            {orcamento!.itens && orcamento!.itens.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-3 font-semibold">
                  Itens Solicitados ({orcamento!.itens.length}):
                </p>
                <ul className="space-y-2">
                  {orcamento!.itens.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="flex w-6 h-6 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold items-center justify-center mr-3 mt-0.5 shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900 font-medium">
                          {item.descricao}
                        </p>
                        {item.descritivo && (
                          <p className="text-xs text-gray-500 mt-1">
                            {item.descritivo}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Quantidade: {item.quantidade} • Diárias: {item.diaria}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit}>
            {/* Seleção do Motivo */}
            <div className="mb-6">
              <label className="block mb-3">
                <span className="text-base font-semibold text-gray-900">
                  Motivo da Recusa <span className="text-red-600">*</span>
                </span>
              </label>

              <div className="space-y-3">
                {/* Opção 1: Não consigo atender ao prazo */}
                <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="motivo"
                    value="prazo"
                    checked={motivoSelecionado === "prazo"}
                    onChange={handleMotivoChange}
                    className="radio radio-primary mt-0.5"
                    disabled={submitting}
                  />
                  <div className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      Não consigo atender ao prazo
                    </span>
                  </div>
                </label>

                {/* Opção 2: Não emito o documento fiscal exigido */}
                <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="motivo"
                    value="documento"
                    checked={motivoSelecionado === "documento"}
                    onChange={handleMotivoChange}
                    className="radio radio-primary mt-0.5"
                    disabled={submitting}
                  />
                  <div className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      Não emito o documento fiscal exigido
                    </span>
                  </div>
                </label>

                {/* Opção 3: Outro motivo */}
                <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="motivo"
                    value="outro"
                    checked={motivoSelecionado === "outro"}
                    onChange={handleMotivoChange}
                    className="radio radio-primary mt-0.5"
                    disabled={submitting}
                  />
                  <div className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      Outro motivo
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Favor informar o motivo abaixo
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Campo de Justificativa (apenas se "Outro motivo" estiver selecionado) */}
            {motivoSelecionado === "outro" && (
              <div className="mb-6">
                <label className="block mb-2">
                  <span className="text-base font-semibold text-gray-900">
                    Justificativa <span className="text-red-600">*</span>
                  </span>
                  <span className="float-right text-sm text-gray-500">
                    {justificativa.length}/1000
                  </span>
                </label>
                <textarea
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                    validationError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                  }`}
                  rows={6}
                  placeholder="Digite o motivo pelo qual não pode atender esta cotação (mínimo 10 caracteres)"
                  value={justificativa}
                  onChange={handleJustificativaChange}
                  disabled={submitting}
                  maxLength={1000}
                />
                {validationError && (
                  <p className="mt-2 text-sm text-red-600">{validationError}</p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  Seja claro e específico sobre o motivo da recusa. Esta
                  informação será compartilhada com o solicitante.
                </p>
              </div>
            )}

            {/* Mensagem de validação geral */}
            {validationError && !motivoSelecionado && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <FiAlertCircle className="text-red-600 w-5 h-5 mr-3 mt-0.5 shrink-0" />
                <span className="text-sm text-red-800">{validationError}</span>
              </div>
            )}

            {/* Mensagem de erro geral */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <FiAlertCircle className="text-red-600 w-5 h-5 mr-3 mt-0.5 shrink-0" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
            )}

            {/* Botões */}
            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-6 border-t border-gray-200">
              <button
                type="button"
                className="btn btn-outline order-2 sm:order-1"
                onClick={handleCancel}
                disabled={submitting}
              >
                <FiArrowLeft className="w-4 h-4 mr-2" />
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary order-1 sm:order-2"
                disabled={
                  submitting ||
                  !motivoSelecionado ||
                  (motivoSelecionado === "outro" &&
                    (justificativa.trim().length < 10 || !!validationError))
                }
              >
                {submitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Enviando...
                  </>
                ) : (
                  <>
                    <FiCheckCircle className="w-4 h-4 mr-2" />
                    Enviar Recusa
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer informativo */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Sistema de Cotação da Monumenta - Todos os direitos reservados</p>
          <p className="mt-1">Em caso de dúvidas, entre em contato conosco</p>
        </div>
      </div>
    </div>
  );
};
